import { defineStore, StoreState, storeToRefs } from "pinia";
import api from "@/services/api";
import { useForeignItemsStore, useItemsStore, useSessionStore, useSitesStore, useSuppliersStore } from '@/stores';
import mainHub from '@/hubs/mainHub';
import type { IOrder, IOrders } from "@/models/responses/IOrder";
import type { IItemOpenQuantityEntry } from "@/models/responses/IItemOpenOrder";
import OrderCreateRequest, { DraftOrderLineFields } from "@/models/requests/OrderCreateRequest";
import CompleteOrderRequest from "@/models/requests/CompleteOrderRequest";
import { OrderHeadStatus, ORDER_HEAD_STATUS } from "@/models/Enums";
import Order from "@/models/frontend/Order";
import OrderLine from "@/models/frontend/OrderLine";

type StoreType = StoreState<{
  error: any;
  data: Order[];
  order: Order | null;
  isLoading: boolean;
  draftLines: DraftOrderLineFields[];
}>;

export const useOrdersStore = defineStore("orders", {
  state: () => ({
    error: "",
    data: [],
    order: null,
    isLoading: false,
    draftLines: [],
  }) as StoreType,
  actions: {
    async fetchAll(includeHistory: boolean = false, statuses: OrderHeadStatus[] = [ORDER_HEAD_STATUS.pending, ORDER_HEAD_STATUS.inProgress]) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      this.error = "";
      this.isLoading = true;
      const statusParams = statuses.map(status => `status=${status}`).join('&');

      await api
        .get<IOrder[]>(`v1.0/orders?siteId=${selectedSiteId.value}&${statusParams}${includeHistory ? "&forcedIncludeTimeSpan=30.00:00:00" : ""}`)
        .then((response) => {
          this.data = response.data.map((order) => this.mapToFrontend(order)).filter((order) => order !== null) as Order[];
          this.isLoading = false;
         })
        .catch((error) => {
          this.error = error;
          this.isLoading = false;
         });
    },
    async fetch(id: number) {
      this.error = "";
      await api
        .get<IOrder>(`v1.0/orders/${id}`)
        .then((response) => {
          const index = this.data.findIndex((x) => x.id === response.data.id);
          const mappedOrder = this.mapToFrontend(response.data);
          if (!mappedOrder) return;
          if (index === -1) this.data.push(mappedOrder);
          else this.data[index] =mappedOrder;
        })
        .catch((error) => (this.error = error));
    },
    async fetchByCode(code: string) {
      this.error = "";
      this.order = null;
      await api
        .get<IOrder>(`v1.0/orders/_${code}`)
        .then((response) => (this.order = this.mapToFrontend(response.data)))
        .catch((error) => (this.error = error));
    },
    async saveDraft(data: DraftOrderLineFields[], onfulfilled: () => void, onrejected: (reason: any) => void | PromiseLike<void>) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      await api
        .post(`v1.0/orders/draft?siteId=${selectedSiteId.value}`, data)
        .then(async () => {
          this.draftLines = [...data];
          // Notify others that the order draft has been changed
          await mainHub.notifyOthersOrderDraftChanged(selectedSiteId.value);
          onfulfilled();
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async loadDraft() {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      return await api
        .get<DraftOrderLineFields[]>(`v1.0/orders/draft?siteId=${selectedSiteId.value}`)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          this.error = error;
        });
    },
    async fillDraftWithDemands() {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      const response = await api.post<boolean>(`v1.0/orders/draft/.fill-demands?siteId=${selectedSiteId.value}`);
      if (response.data) {
        await mainHub.notifyOthersOrderDraftChanged(selectedSiteId.value);
      }
      return response.data === true;
    },
    async fetchDraftLines() {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      if (!selectedSiteId.value) {
        this.draftLines = [];
        return;
      }

      try {
        const response = await api.get<DraftOrderLineFields[]>(`v1.0/orders/draft?siteId=${selectedSiteId.value}`);
        this.draftLines = response.data ?? [];
      } catch (error) {
        console.error("Error loading draft lines:", error);
        this.draftLines = [];
      }
    },
    async create(order: OrderCreateRequest, onfulfilled: (data: Order[]) => void, onrejected: (reason: any) => void | PromiseLike<void>) {
      await api
        .post<IOrders>("v1.0/orders", order)
        .then((response) => {
          const mappedResponse = response.data.orders.map((order) => this.mapToFrontend(order)).filter((order) => order !== null) as Order[];
          mappedResponse.forEach((order) => {
            const index = this.data.findIndex((x) => x.id === order.id);
            if (index === -1) this.data.push(order);
            else this.data[index] = order;
          });
          onfulfilled(mappedResponse);
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async accept(id: number, deliveryDate: Date | undefined, onfulfilled: () => void | PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .put(`v1.0/orders/${id}/accept`, {
          deliveryDate: deliveryDate
        })
        .then(async () => {
          await this.fetch(id);
          await onfulfilled();
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async complete(id: number, data: CompleteOrderRequest, onfulfilled: () => void | PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .put(`v1.0/orders/${id}/complete`, data)
        .then(async () => {
          await this.fetch(id);
          await onfulfilled();
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async cancel(id: number, onfulfilled: () => void | PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .put(`v1.0/orders/${id}/cancel`)
        .then(async () => {
          await this.fetch(id);
          await onfulfilled();
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async reorder(id: number, onfulfilled: (data: Order) => void | PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .post<IOrder>(`v1.0/orders/${id}/reorder`)
        .then(async (response) => {
          const index = this.data.findIndex((x) => x.id === response.data.id);
          const mappedOrder = this.mapToFrontend(response.data);
          if (!mappedOrder) return;
          if (index === -1) this.data.push(mappedOrder);
          else this.data[index] = mappedOrder;
          await onfulfilled(mappedOrder);
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async redraft(id: number, onfulfilled: () => void | PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .post(`v1.0/orders/${id}/redraft`)
        .then(async () => {
          await onfulfilled();
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async ignore(id: number, onfulfilled: (data: Order) => void | PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .post<IOrder>(`v1.0/orders/${id}/ignore`)
        .then(async () => {
          const index = this.data.findIndex((x) => x.id === id);
          this.data[index]!.completeReason = 'partial-ignore',
          await onfulfilled(this.data[index]!);
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async resend(id: number, onfulfilled: () => void | PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .post(`v1.0/orders/${id}/resend`)
        .then(async () => {
          await onfulfilled();
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async getHtmlMail(id: number, culture?: string) {
      const url = culture
        ? `v1.0/orders/${id}/html-mail?culture=${encodeURIComponent(culture)}`
        : `v1.0/orders/${id}/html-mail`;
      return await api.get<string>(url)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Error fetching HTML mail:", error);
        });
    },
    async delete(id: number) {
      this.error = "";
      await api
        .delete(`v1.0/orders/${id}`)
        .then(() => {
          this.data = this.data.filter((order) => order.id !== id);
        })
        .catch((error) => (this.error = error));
    },
    mapToFrontend(order: IOrder): Order | null {
      try {
        const site = useSitesStore().getById(order.siteId);
        const supplier = useSuppliersStore().getById(order.supplierId);
        const itemsStore = useItemsStore();
        const foreignItemsStore = useForeignItemsStore();
        const lines = order.lines.map((line) => {
          const item = itemsStore.getById(line.itemId);
          if (!item) {
            console.warn(`Item with ID ${line.itemId} not found for order line for order #${order.id} "${order.code}".`);
            return null; // Skip this line if item is not found
          }
          const foreignItem = foreignItemsStore.getByItemId(item.id);
          return new OrderLine(
            item,
            line.quantity,
            line.unitCode,
            line.deliveredQuantity,
            line.foreignItemCode,
            line.supplierItemCode,
            line.unitName,
            line.deliveredLot,
            line.deliveredBestBefore,
            line.deliveredExpiry,
            foreignItem);
        }).filter((line) => line !== null) as OrderLine[];
        return new Order(
          order.id,
          site,
          order.code,
          order.status,
          order.completeReason,
          supplier,
          lines,
          order.audit,
          order.exposedOrderToken,
          order.deliveryDate);
      }
      catch (error) {
        console.error("Error mapping order to frontend:", error);
        console.log("Order data:", order);
        return null;
      }
    },
  },
  getters: {
    getById: (state: StoreType) =>
      (id: number) => state.data.find((order) => order.id === id),
    getByCode: (state: StoreType) =>
      (code: string, siteId: number): Order | undefined => {
        if (state.order?.code === code) return state.order;
        return state.data.find((order) => order.code === code && order.site.id === siteId);
      },
    draftQuantityByItemId: (state: StoreType): Record<number, IItemOpenQuantityEntry[]> => {
      const result: Record<number, IItemOpenQuantityEntry[]> = {};
      for (const line of state.draftLines) {
        const entries = result[line.itemId] ?? (result[line.itemId] = []);
        const existing = entries.find((e) => e.unitCode === line.unitCode);
        if (existing) {
          existing.quantity += line.quantity;
        } else {
          entries.push({ quantity: line.quantity, unitCode: line.unitCode });
        }
      }
      return result;
    },
  },
});
